import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Components/Header';
import CitySearch from './Features/CitySearch';
import DateSelector from './Features/DateSelector';
import MapComponent from './Components/MapComponent';
import EventList from './Components/EventList';
import { fetchTicketmaster } from './Services/ticketmasterService';
import {
  clearInterestedEventsById,
  loadInterestedEventsById,
  saveInterestedEventsById,
} from './Services/interestedEventsService';
import {
  deleteSavedEvent,
  fetchSavedEventsById,
  syncSavedEvents,
  upsertSavedEvent,
} from './Services/savedEventsService';
import { getQueryParams } from './utils/queryUtils';
import { Search } from 'lucide-react';
import { useAuth } from './context/AuthContext';
import { trackEvent } from './lib/analytics';
import './EventsPage.css';

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [center, setCenter] = useState({ lat: 37.7749, lng: -122.4194 });
  const [city, setCity] = useState('');
  const [dateRange, setDateRange] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchKind, setSearchKind] = useState('city');
  const [venueQuery, setVenueQuery] = useState('');
  const [venueCoords, setVenueCoords] = useState(null);
  const [activeEventId, setActiveEventId] = useState(null);
  const [hoveredEventId, setHoveredEventId] = useState(null);
  const [showFullSearch, setShowFullSearch] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [interestedEventsById, setInterestedEventsById] = useState(() => loadInterestedEventsById());
  const [lotteryEntryVersion, setLotteryEntryVersion] = useState(0);
  const [savedEventsLoaded, setSavedEventsLoaded] = useState(false);
  const [localSavedEventsMigrated, setLocalSavedEventsMigrated] = useState(false);
  const prevCityRef = useRef('');
  const prevDatesRef = useRef({ start: '', end: '' });
  const pendingCollapseRef = useRef(false);
  const { user, isLoading: authLoading } = useAuth();

  const [drawerState, setDrawerState] = useState('min');

  const location = useLocation();
  const navigate = useNavigate();
  const drawerRef = useRef(null);
  const handleRef = useRef(null);

  /* Utils for drawer sizing */
  const vhToPx = useCallback((vh) => Math.round((window.innerHeight * vh) / 100), []);
  const getHeaderMobile = useCallback(() => {
    const el = document.querySelector('.events-page .header-wrapper');
    return el ? el.offsetHeight : 48;
  }, []);
  const getMaxDrawerHeight = useCallback(() => {
    const headerH = getHeaderMobile();
    return Math.min(vhToPx(90), window.innerHeight - headerH - 8);
  }, [getHeaderMobile, vhToPx]);
  const getMinDrawerHeight = useCallback(() => vhToPx(10), [vhToPx]);
  const getMidDrawerHeight = useCallback(() => vhToPx(50), [vhToPx]);

  /* Detect mobile */
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);


  useEffect(() => {
    const drawer = drawerRef.current;
    if (!drawer) return;

    if (isMobile) {
      const minH = getMinDrawerHeight();
      drawer.style.height = `${minH}px`;
      drawer.classList.add('drawer--min');
      drawer.classList.remove('drawer--mid', 'drawer--max');
      setDrawerState('min');
    } else {
      // desktop shows full right pane naturally
      drawer.style.height = '';
      drawer.classList.remove('drawer--min', 'drawer--mid', 'drawer--max', 'is-dragging');
      setDrawerState('min');
    }
  }, [isMobile, getMinDrawerHeight]);

  /* Tap handle to cycle (mobile only) */
  const cycleDrawer = useCallback(() => {
    if (!isMobile) return;
    const drawer = drawerRef.current;
    if (!drawer) return;

    const minH = getMinDrawerHeight();
    const midH = getMidDrawerHeight();
    const maxH = getMaxDrawerHeight();

    let next = midH;
    if (drawerState === 'min') next = midH;
    else if (drawerState === 'mid') next = maxH;
    else next = midH;

    drawer.style.height = `${Math.round(next)}px`;
    drawer.classList.remove('drawer--min', 'drawer--mid', 'drawer--max');
    if (next === minH) drawer.classList.add('drawer--min');
    else if (next === midH) drawer.classList.add('drawer--mid');
    else drawer.classList.add('drawer--max');

    setDrawerState(next === minH ? 'min' : next === midH ? 'mid' : 'max');
  }, [isMobile, drawerState, getMinDrawerHeight, getMidDrawerHeight, getMaxDrawerHeight]);


  useEffect(() => {
    if (!isMobile) return;

    const drawer = drawerRef.current;
    if (!drawer) return;

    const grabHandle = handleRef.current || drawer;
    const headerHandle = drawer.querySelector('.event-list-header');
    const handles = [grabHandle].concat(headerHandle ? [headerHandle] : []);

    let startY = 0;
    let startHeight = 0;
    let dragging = false;
    let didMove = false;
    let raf = null;
    const THRESHOLD = 4;

    const isInteractive = (el) =>
      !!el.closest('button, a, input, select, textarea, label, [role="button"], .no-drag');

    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

    const setHeight = (h) => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        drawer.style.height = `${Math.round(h)}px`;
      });
    };

    const startDrag = (y, target) => {
      if (headerHandle && headerHandle.contains(target) && isInteractive(target)) return false;

      dragging = true;
      didMove = false;
      drawer.classList.add('is-dragging');
      startY = y;
      startHeight = drawer.getBoundingClientRect().height;
      return true;
    };

    const moveDrag = (y, ev) => {
      if (!dragging) return;
      const dy = y - startY;
      if (Math.abs(dy) >= THRESHOLD) didMove = true;

      const minH = getMinDrawerHeight();
      const maxH = getMaxDrawerHeight();
      let next = startHeight - dy;
      next = clamp(next, minH, maxH);
      setHeight(next);

      ev?.preventDefault?.();
      ev?.stopPropagation?.();
    };

    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      drawer.classList.remove('is-dragging');

      const minH = getMinDrawerHeight();
      const midH = getMidDrawerHeight();
      const maxH = getMaxDrawerHeight();

      const current = drawer.getBoundingClientRect().height;
      const stops = [minH, midH, maxH];
      let nearest = stops[0];
      let best = Math.abs(current - stops[0]);
      for (let i = 1; i < stops.length; i++) {
        const d = Math.abs(current - stops[i]);
        if (d < best) { best = d; nearest = stops[i]; }
      }

      drawer.style.height = `${Math.round(nearest)}px`;
      drawer.classList.remove('drawer--min', 'drawer--mid', 'drawer--max');
      if (nearest === minH) { drawer.classList.add('drawer--min'); setDrawerState('min'); }
      else if (nearest === midH) { drawer.classList.add('drawer--mid'); setDrawerState('mid'); }
      else { drawer.classList.add('drawer--max'); setDrawerState('max'); }
    };

    const opts = { passive: false };

    const bindHandle = (h) => {
      if (!h) return { unbind: () => {} };

      // Pointer (preferred)
      const onPointerDown = (e) => {
        startDrag(e.clientY, e.target);
        try { h.setPointerCapture(e.pointerId); } catch {}
      };
      const onPointerMove = (e) => moveDrag(e.clientY, e);
      const onPointerUp = () => endDrag();

      const onTouchStart = (e) => startDrag(e.touches[0].clientY, e.target);
      const onTouchMove = (e) => moveDrag(e.touches[0].clientY, e);
      const onTouchEnd = () => endDrag();

      const onClick = (e) => {
        if (didMove) return;
        if (headerHandle && headerHandle.contains(e.target) && isInteractive(e.target)) {
          return;
        }
        cycleDrawer();
        e.stopPropagation();
      };

      h.addEventListener('pointerdown', onPointerDown, opts);
      document.addEventListener('pointermove', onPointerMove, opts);
      document.addEventListener('pointerup', onPointerUp, opts);

      h.addEventListener('touchstart', onTouchStart, opts);
      document.addEventListener('touchmove', onTouchMove, opts);
      document.addEventListener('touchend', onTouchEnd, opts);

      h.addEventListener('click', onClick);

      return {
        unbind: () => {
          h.removeEventListener('pointerdown', onPointerDown, opts);
          document.removeEventListener('pointermove', onPointerMove, opts);
          document.removeEventListener('pointerup', onPointerUp, opts);

          h.removeEventListener('touchstart', onTouchStart, opts);
          document.removeEventListener('touchmove', onTouchMove, opts);
          document.removeEventListener('touchend', onTouchEnd, opts);

          h.removeEventListener('click', onClick);
        }
      };
    };

    const bindings = handles.map(bindHandle);

    return () => {
      bindings.forEach(b => b.unbind());
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isMobile, drawerState, getMinDrawerHeight, getMidDrawerHeight, getMaxDrawerHeight, cycleDrawer]);

  useEffect(() => {
    if (!isMobile) return;
    const onResize = () => {
      const drawer = drawerRef.current;
      if (!drawer) return;
      const minH = getMinDrawerHeight();
      const midH = getMidDrawerHeight();
      const maxH = getMaxDrawerHeight();
      let target = minH;
      if (drawerState === 'mid') target = midH;
      if (drawerState === 'max') target = maxH;
      drawer.style.height = `${Math.round(target)}px`;
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [isMobile, drawerState, getMinDrawerHeight, getMidDrawerHeight, getMaxDrawerHeight]);

  /* Data loading */
  const loadEventsBySearch = async ({ kind, cityArg, venueArg, startArg, endArg, coords }) => {
    if (!startArg || !endArg) return;
    const query = kind === 'venue' ? venueArg : cityArg;
    if (!query && (!coords || coords.lat == null || coords.lng == null)) return;
    setLoading(true);
    try {
      let lat = coords?.lat;
      let lng = coords?.lng;

      if (lat == null || lng == null) {
        const geoRes = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query)}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
        );
        const geoData = await geoRes.json();
        const loc = geoData.results?.[0]?.geometry?.location;
        if (!loc) throw new Error('Geocoding failed');
        lat = loc.lat;
        lng = loc.lng;
      }

      const radius = kind === 'venue' ? 3 : 25;
      const fetchedEvents = await fetchTicketmaster(
        lat,
        lng,
        startArg,
        endArg,
        radius,
        kind === 'venue' ? venueArg : null
      );

      const normalizedVenue = venueArg ? venueArg.trim().toLowerCase() : '';
      const finalEvents =
        kind === 'venue' && normalizedVenue
          ? fetchedEvents.filter((event) => {
              const venueName = event._embedded?.venues?.[0]?.name || '';
              return venueName.toLowerCase().includes(normalizedVenue);
            })
          : fetchedEvents;

      setEvents(finalEvents);
      setActiveEventId(null);

      if (finalEvents.length > 0 && finalEvents[0]._embedded?.venues?.[0]?.location) {
        const { latitude, longitude } = finalEvents[0]._embedded.venues[0].location;
        setCenter({ lat: parseFloat(latitude), lng: parseFloat(longitude) });
      } else {
        setCenter({ lat, lng });
      }
    } catch (error) {
      console.error('Failed to load events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMapMove = async ({ lat, lng, radius }) => {
    try {
      const fetchedEvents = await fetchTicketmaster(
        lat,
        lng,
        startDate,
        endDate,
        radius,
        searchKind === 'venue' ? venueQuery : null
      );

      const normalizedVenue = venueQuery ? venueQuery.trim().toLowerCase() : '';
      const finalEvents =
        searchKind === 'venue' && normalizedVenue
          ? fetchedEvents.filter((event) => {
              const venueName = event._embedded?.venues?.[0]?.name || '';
              return venueName.toLowerCase().includes(normalizedVenue);
            })
          : fetchedEvents;

      setEvents(finalEvents);
      setActiveEventId(null);

      if (finalEvents.length > 0 && finalEvents[0]._embedded?.venues?.[0]?.location) {
        const { latitude, longitude } = finalEvents[0]._embedded.venues[0].location;
        setCenter({ lat: parseFloat(latitude), lng: parseFloat(longitude) });
      } else {
        setCenter({ lat, lng });
      }
    } catch (err) {
      console.error('Failed to fetch events after map move:', err);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const hasDates = startDate || dateRange;
    if (searchKind === 'venue') {
      if (venueQuery && hasDates) {
        trackEvent('search_submitted', {
          search_origin: 'events_page',
          search_kind: 'venue',
          city,
          venue_query: venueQuery,
          date_range: dateRange || 'custom',
          start_date: startDate,
          end_date: endDate,
        });
        const params = new URLSearchParams();
        params.set('venue', venueQuery);
        if (venueCoords?.lat != null && venueCoords?.lng != null) {
          params.set('lat', String(venueCoords.lat));
          params.set('lng', String(venueCoords.lng));
        }
        if (startDate) params.set('startDate', startDate);
        if (endDate) params.set('endDate', endDate);
        if (dateRange) params.set('dateRange', dateRange);
        navigate(`/events?${params.toString()}`);
      } else {
        alert('Please fill out all fields.');
      }
      return;
    }

    if (city && hasDates) {
      trackEvent('search_submitted', {
        search_origin: 'events_page',
        search_kind: 'city',
        city,
        date_range: dateRange || 'custom',
        start_date: startDate,
        end_date: endDate,
      });
      const params = new URLSearchParams();
      params.set('city', city);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (dateRange) params.set('dateRange', dateRange);
      navigate(`/events?${params.toString()}`);
    } else {
      alert('Please fill out all fields.');
    }
  };

  /* Parse URL query on first load */
  useEffect(() => {
    const params = getQueryParams(location.search);
    const cityParam = params.get('city');
    const venueParam = params.get('venue');
    const latParam = params.get('lat');
    const lngParam = params.get('lng');
    const startDateParam = params.get('startDate');
    const endDateParam = params.get('endDate');
    const dateRangeParam = params.get('dateRange');

    setCity(cityParam || '');
    setStartDate(startDateParam || '');
    setEndDate(endDateParam || '');
    setDateRange(dateRangeParam || '');

    if (venueParam) {
      setSearchKind('venue');
      setVenueQuery(venueParam);
      if (latParam && lngParam) {
        const parsedLat = Number(latParam);
        const parsedLng = Number(lngParam);
        if (Number.isFinite(parsedLat) && Number.isFinite(parsedLng)) {
          setVenueCoords({ lat: parsedLat, lng: parsedLng });
        } else {
          setVenueCoords(null);
        }
      } else {
        setVenueCoords(null);
      }
    } else {
      setSearchKind('city');
      setVenueQuery('');
      setVenueCoords(null);
    }

    if ((venueParam || cityParam) && startDateParam && endDateParam) {
      loadEventsBySearch({
        kind: venueParam ? 'venue' : 'city',
        cityArg: cityParam,
        venueArg: venueParam,
        startArg: startDateParam,
        endArg: endDateParam,
        coords:
          venueParam && latParam && lngParam
            ? { lat: Number(latParam), lng: Number(lngParam) }
            : null,
      });
    }
  }, [location.search]);

  /* Auto-refetch on date/city change */
  useEffect(() => {
    loadEventsBySearch({
      kind: searchKind,
      cityArg: city,
      venueArg: venueQuery,
      startArg: startDate,
      endArg: endDate,
      coords: venueCoords,
    });
  }, [city, startDate, endDate, searchKind, venueQuery, venueCoords]);

  /* Track city changes to collapse search on mobile */
  useEffect(() => {
    if (!city || !isMobile) {
      prevCityRef.current = city;
      if (!isMobile) pendingCollapseRef.current = false;
      return;
    }
    if (city !== prevCityRef.current) {
      pendingCollapseRef.current = true;
      prevCityRef.current = city;
    }
  }, [city, isMobile]);

  useEffect(() => {
    if (!isMobile) {
      prevDatesRef.current = { start: startDate, end: endDate };
      pendingCollapseRef.current = false;
      return;
    }
    const prev = prevDatesRef.current;
    if (startDate !== prev.start || endDate !== prev.end) {
      pendingCollapseRef.current = true;
      prevDatesRef.current = { start: startDate, end: endDate };
    }
  }, [startDate, endDate, isMobile]);

  useEffect(() => {
    if (!pendingCollapseRef.current) return;
    if (!isMobile || loading) {
      if (!isMobile) pendingCollapseRef.current = false;
      return;
    }
    if (showFullSearch) {
      setShowFullSearch(false);
    }
    pendingCollapseRef.current = false;
  }, [events, isMobile, showFullSearch, loading]);

  useEffect(() => {
    let active = true;

    if (authLoading) return undefined;

    if (!user) {
      setInterestedEventsById(loadInterestedEventsById());
      setLocalSavedEventsMigrated(false);
      setSavedEventsLoaded(true);
      return undefined;
    }

    setSavedEventsLoaded(false);

    const loadSavedEvents = async () => {
      try {
        const localEvents = loadInterestedEventsById();
        const remoteEvents = await fetchSavedEventsById(user.id);
        let nextEvents = remoteEvents;

        if (!localSavedEventsMigrated && Object.keys(localEvents).length > 0) {
          nextEvents = { ...localEvents, ...remoteEvents };
          await syncSavedEvents(user.id, nextEvents);
          clearInterestedEventsById();
          if (active) {
            setLocalSavedEventsMigrated(true);
          }
        } else if (!localSavedEventsMigrated && active) {
          setLocalSavedEventsMigrated(true);
        }

        if (active) {
          setInterestedEventsById(nextEvents);
        }
      } catch (error) {
        console.error('Failed to load saved events:', error);
      } finally {
        if (active) {
          setSavedEventsLoaded(true);
        }
      }
    };

    loadSavedEvents();

    return () => {
      active = false;
    };
  }, [user, authLoading, localSavedEventsMigrated]);

  useEffect(() => {
    if (authLoading || user) return;
    saveInterestedEventsById(interestedEventsById);
  }, [interestedEventsById, user, authLoading]);

  const handleLocationUpdate = useCallback((payload) => {
    if (!payload) return;
    if (payload.type === 'venue') {
      setSearchKind('venue');
      setVenueQuery(payload.venueName || payload.display || '');
      if (payload.city) setCity(payload.city);
      if (payload.lat != null && payload.lng != null) {
        setVenueCoords({ lat: payload.lat, lng: payload.lng });
      } else {
        setVenueCoords(null);
      }
      return;
    }

    if (payload.type === 'city') {
      setSearchKind('city');
      setVenueQuery('');
      setVenueCoords(null);
      if (payload.city) setCity(payload.city);
      return;
    }

    if (payload.selected === false) {
      setSearchKind('city');
      setVenueQuery('');
      setVenueCoords(null);
    }
  }, []);

  const toggleInterestedEvent = useCallback((event) => {
    if (!event?.id) return;

    let wasSaved = false;

    setInterestedEventsById((prev) => {
      wasSaved = !!prev[event.id];

      if (prev[event.id]) {
        const { [event.id]: _removed, ...next } = prev;
        return next;
      }

      return { ...prev, [event.id]: event };
    });

    trackEvent(wasSaved ? 'event_unsaved' : 'event_saved', {
      source: 'events_page',
      event_id: event.id,
      event_name: event.name,
      event_category: event.classifications?.[0]?.segment?.name,
      event_genre: event.classifications?.[0]?.genre?.name,
    });

    if (!user) return;

    clearInterestedEventsById();

    (async () => {
      try {
        if (wasSaved) {
          await deleteSavedEvent(user.id, event.id);
          return;
        }

        await upsertSavedEvent(user.id, event);
      } catch (error) {
        console.error('Failed to sync saved event:', error);
        setInterestedEventsById((prev) => {
          if (wasSaved) {
            return { ...prev, [event.id]: event };
          }

          const { [event.id]: _removed, ...next } = prev;
          return next;
        });
        alert('We could not update your saved events. Please try again.');
      }
    })();
  }, [user]);

  return (
    <div className="events-page">
      <div className="header-wrapper">
        <Header />
      </div>

      <div className="main-content">
        <div className="left-column">
          {isMobile ? (
            showFullSearch ? (
              <form
                className="search-form expanded"
                onSubmit={(e) => {
                  handleSearch(e);
                  setShowFullSearch(false);
                }}
              >
                <div className="search-form-row">
                  <CitySearch city={city} setCity={setCity} onLocation={handleLocationUpdate} />
                  <DateSelector
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                  />
                </div>
                <button type="submit" className="search-button">Search</button>
              </form>
            ) : (
              <div className="collapsed-search-bar" onClick={() => setShowFullSearch(true)}>
                <Search className="search-icon" size={20} />
                <span>Search city, venue, or date</span>
              </div>
            )
          ) : (
            <form className="search-form expanded" onSubmit={handleSearch}>
              <div className="search-form-row">
                <CitySearch city={city} setCity={setCity} onLocation={handleLocationUpdate} />
                <DateSelector
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                />
              </div>
              <button type="submit" className="search-button">Search</button>
            </form>
          )}

          <div className="map-container">
            {/* pass hoveredEventId through */}
            <MapComponent
              center={center}
              events={events}
              loading={loading}
              activeEventId={activeEventId}
              setActiveEventId={setActiveEventId}
              hoveredEventId={hoveredEventId}
              onMapMove={handleMapMove}
            />
          </div>
        </div>

        <div
          className={`event-list-container drawer--${drawerState}`}
          ref={drawerRef}
        >
          {/* Mobile-only: big invisible grab region + visual handle */}
          {isMobile && (
            <>
              <div className="drawer-grab-region" ref={handleRef} />
              <div className="drawer-handle" />
            </>
          )}

          <EventList
            events={events}
            loading={loading || (user ? !savedEventsLoaded : false)}
            canSave={!!user}
            activeEventId={activeEventId}
            setActiveEventId={setActiveEventId}
            hoveredEventId={hoveredEventId}
            setHoveredEventId={setHoveredEventId}
            startDate={startDate}
            interestedEventsById={interestedEventsById}
            onToggleInterested={toggleInterestedEvent}
            lotteryEntryVersion={lotteryEntryVersion}
            onLotteryEntered={() => setLotteryEntryVersion((prev) => prev + 1)}
          />
        </div>
      </div>
    </div>
  );
};

export default EventsPage;
